import * as React from "react";

import { ScriptGenerationGateDialog } from "@/features/opportunities/components/table/BulkActionBar/ScriptGenerationGateDialog";

import {
  gateCompetitorScriptTargets,
  type CompetitorScriptGate,
} from "../../../lib/competitor-script-gate.js";
import type { GenerateScriptsTarget } from "../../workspace/generate-scripts/GenerateScriptsModal.js";
import type { HubChromeOpportunityRow } from "../PersonaOpportunityHubChrome/hub-chrome.types.js";

export interface UseCompetitorScriptGateArgs {
  rows: readonly HubChromeOpportunityRow[];
  onGenerate: (targets: GenerateScriptsTarget[]) => void;
  onShowTopics: () => void;
  /** Called when the gate closes without continuing, so focus can return to Generate Scripts. */
  onDismissed?: () => void;
}

/** Routes a generate request through the Topics-only gate before the generate modal opens. */
export function useCompetitorScriptGate({
  rows,
  onGenerate,
  onShowTopics,
  onDismissed,
}: UseCompetitorScriptGateArgs) {
  const [blocked, setBlocked] = React.useState<CompetitorScriptGate | null>(null);
  const datasetById = React.useMemo(
    () => new Map(rows.map((row) => [row.id, row.dataset])),
    [rows],
  );

  const request = (targets: GenerateScriptsTarget[]) => {
    const gate = gateCompetitorScriptTargets(targets, datasetById);
    if (gate.ineligibleCount === 0) {
      onGenerate(targets);
      return;
    }
    setBlocked(gate);
  };

  const dialog = blocked ? (
    <ScriptGenerationGateDialog
      open
      onOpenChange={(open) => {
        if (!open) {
          setBlocked(null);
          onDismissed?.();
        }
      }}
      mode={blocked.eligibleCount > 0 ? "partial" : "all_ineligible"}
      selectedCount={blocked.selectedCount}
      eligibleCount={blocked.eligibleCount}
      ineligibleCount={blocked.ineligibleCount}
      unknownCount={0}
      ineligibleDatasets={blocked.ineligibleDatasets}
      onSwitchToTopics={() => {
        setBlocked(null);
        onShowTopics();
      }}
      onGenerateEligible={() => {
        setBlocked(null);
        onGenerate(blocked.eligibleTargets);
      }}
    />
  ) : null;

  return { request, dialog };
}
